/**
 * Minimal Server-Sent Events parser for streaming chat responses.
 *
 * The orchestrator emits events shaped like:
 *   data: {"type":"meta","economist_slug":"zhangsan","model":"gemini-2.0-flash"}\n\n
 *   data: {"type":"delta","text":"Hello"}\n\n
 *   data: {"type":"delta","text":" world"}\n\n
 *   data: {"type":"done","message_id":"msg_..."}\n\n
 *
 * We deliberately keep this dependency-free and side-effect-free so it can be
 * unit-tested with a synthetic ReadableStream in Vitest under the Node env.
 */
export type SseEvent =
  | { type: 'meta'; economist_slug?: string; model?: string; [k: string]: unknown }
  | { type: 'delta'; text: string }
  | { type: 'done'; message_id?: string; [k: string]: unknown }
  | { type: 'error'; message?: string; [k: string]: unknown }
  | { type: string; [k: string]: unknown };

/**
 * Read an SSE response stream and invoke `onEvent` for every parsed `data:`
 * line. Returns when the upstream stream ends. The caller is expected to keep
 * its own state (assistant message buffer, etc).
 *
 * The function is tolerant of:
 *   - chunks that split an event in the middle (we buffer until `\n\n`)
 *   - `data:` with or without a space after the colon
 *   - non-`data:` lines (comments, retry hints) — silently skipped
 *   - malformed JSON — surfaces as an `error` event so the UI can react
 */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: SseEvent) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE event delimiter is a blank line: `\n\n` (or `\r\n\r\n`).
      // We normalize CRLF to LF before splitting so we don't have to handle both.
      const norm = buf.replace(/\r\n/g, '\n');
      const parts = norm.split('\n\n');
      buf = parts.pop() ?? '';
      for (const raw of parts) {
        const ev = parseSseEvent(raw);
        if (ev) onEvent(ev);
      }
    }
    // Flush any trailing event without a terminating blank line.
    if (buf.trim().length > 0) {
      const ev = parseSseEvent(buf);
      if (ev) onEvent(ev);
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Parse a single SSE event block (one or more lines ending before `\n\n`).
 * Only `data:` lines are inspected — `event:`, `id:`, `retry:` are ignored.
 * Multi-line `data:` is concatenated with `\n` per the SSE spec.
 */
export function parseSseEvent(block: string): SseEvent | null {
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('data:')) {
      // Strip leading `data:` and an optional single space.
      const v = line.slice(5);
      dataLines.push(v.startsWith(' ') ? v.slice(1) : v);
    }
  }
  if (dataLines.length === 0) return null;
  const payload = dataLines.join('\n');
  // R77-04 (Codex Round 77 recheck, 2026-06-29) — when an LLM output ban
  // collides with hermes failure path, we sometimes see a hermes-side
  // SSE chunk that isn't valid JSON (e.g. a partial chunk that got split
  // across CF Worker buffer boundaries, or a keepalive). Previously this
  // surfaced as a page-level「AI 没回复」+ sse_parse_error to the user.
  // Better: log + skip the unparseable line; let the stream continue.
  // Critical event types (final 'done' / 'error') are JSON-emitted by
  // hermes/orchestrator so we won't lose them — only mid-stream noise.
  if (!payload.trim()) return null;
  if (payload === '[DONE]') return { type: 'done' };
  try {
    return JSON.parse(payload) as SseEvent;
  } catch {
    if (typeof console !== 'undefined') {
      console.warn('[sse] non-JSON data chunk dropped:', payload.slice(0, 200));
    }
    // Treat as no-op so the rest of the stream can finish.
    return null;
  }
}
